import React, { ReactNode, ElementType, forwardRef, ForwardedRef, ComponentType } from 'react';
import {
  Stack,
  Box,
  Button,
  Typography,
  StackProps,
  TypographyProps,
  ButtonProps,
  BoxProps,
  SvgIconProps,
} from '@mui/material';
import { ArrowRightIcon } from '@/src/components/icon/stroke/ArrowRightIcon';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export interface QuickLinkProps extends Omit<StackProps<ElementType>, 'title'> {
  /**
   * Icon node to be used
   */
  IconComponent?: ComponentType<SvgIconProps>;
  /**
   * Props applied to Icon component
   */
  iconProps?: SvgIconProps;
  /**
   * Title text
   */
  title?: ReactNode;
  /**
   * Props applied to title Typography wrapper component
   */
  titleTypographyProps?: TypographyProps<ElementType>;
  /**
   * Subtitle text
   */
  subtitle?: ReactNode;
  /**
   * Props applied to subtitle Typography wrapper component
   */
  subtitleTypographyProps?: TypographyProps<ElementType>;
  /**
   * Props applied to wrapper of divider component
   */
  dividerWrapperProps?: BoxProps<ElementType>;
  /**
   * Props applied to divider component
   */
  dividerProps?: BoxProps<ElementType>;
  /**
   * Content wrapper
   */
  contentWrapperProps?: BoxProps<ElementType>;
  /**
   * Icon and Title wrapper that changes on the mobile
   */
  titleWrapperProps?: StackProps<ElementType>;
  /**
   * Button text
   */
  buttonTitle?: ReactNode;
  /**
   * Props applied to Button component
   */
  buttonProps?: ButtonProps;
}

/**
 * Quick Links are used to provide immediate access to the most relevant and
 * frequently used actions related to a product or service.
 *
 * Composition of title, subtitle, icon, and button
 */
export const QuickLink: React.FC<QuickLinkProps> = forwardRef(
  (
    {
      IconComponent,
      iconProps,
      title,
      titleTypographyProps,
      subtitle,
      subtitleTypographyProps,
      dividerWrapperProps,
      dividerProps,
      contentWrapperProps,
      titleWrapperProps,
      buttonTitle,
      buttonProps,
      buttonIconProps,
      onClick,
      sx,
      ...rest
    },
    ref,
  ) => {
    const { sx: iconSx, ...restIconProps } = iconProps || {};
    const { sx: dividerWrapperSx, ...restDividerWrapperProps } = dividerWrapperProps || {};
    const { sx: dividerSx, ...restDividerProps } = dividerProps || {};
    const { sx: contentWrapperSx, ...restContentWrapperProps } = contentWrapperProps || {};
    const { sx: titleWrapperSx, ...restTitleWrapperProps } = titleWrapperProps || {};

    return (
      <Stack
        direction="column"
        alignItems="flex-start"
        sx={{
          minWidth: '0',
          cursor: 'pointer',
          color: 'inherit',
          textDecoration: 'unset',
          gap: { xs: '8px', md: '16px' },
          ...getInteractiveStyle(
            {
              hr: {
                backgroundColor: 'text.primary',
                height: '1px',
              },
              // interactive style from button.theme
              '.MuiButton-navigation': {
                backgroundColor: 'transparent',
                color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os0' : 'onSurface.os900'),
                '& .MuiButton-startIcon': {
                  transform: 'translateX(0px)',
                },
                '& .MuiButton-endIcon': {
                  transform: 'translateX(0px)',
                },
              },
            },
            {
              hr: {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'brand.secondary' : 'brand.primary'),
                height: '2px',
              },
              '.MuiButton-navigation': {
                backgroundColor: 'transparent',
                color: (theme) => (theme.palette.mode === 'dark' ? 'onSurface.os300' : 'onSurface.os700'),
                '.MuiButton-startIcon': {
                  transform: 'translateX(-8px)',
                },
                '.MuiButton-endIcon': {
                  transform: 'translateX(8px)',
                },
              },
            },
          ),
          ...sx,
        }}
        data-testid="QuickLink"
        ref={ref as ForwardedRef<HTMLDivElement>}
        {...(onClick && {
          onClick: onClick,
          role: 'button',
        })}
        tabIndex={-1}
        {...rest}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '2px',
            // 32px gap from top if icon is hidden
            '&&': { mb: { xs: 0, md: IconComponent ? 0 : 2 } },
            ...dividerWrapperSx,
          }}
          {...restDividerWrapperProps}
        >
          <Box
            component="hr"
            sx={{
              border: 'none',
              position: 'absolute',
              inset: '0',
              m: 0,
              // style is set via interactive parent
              ...dividerSx,
            }}
            {...restDividerProps}
          />
        </Box>
        <Box
          sx={{ width: '480px', maxWidth: '100%', pr: 3, pt: { xs: '8px', md: '0px' }, ...contentWrapperSx }}
          {...restContentWrapperProps}
        >
          <Stack
            sx={{
              flexDirection: { xs: 'row', md: 'column' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: { xs: '4px', md: '18px' },
              mb: { xs: '4px', md: 0 },
              ...titleWrapperSx,
            }}
            {...restTitleWrapperProps}
          >
            {IconComponent && (
              <IconComponent
                sx={{
                  display: 'block',
                  fontSize: { xs: '20px', md: '35px' },
                  mx: { xs: '2px', md: '0px' },
                  my: { xs: '0px', md: '2px' },
                  ...iconSx,
                }}
                data-testid="QuickLink.icon"
                {...restIconProps}
              />
            )}
            {title && (
              <Typography
                variant="h4"
                display="block"
                data-testid="QuickLink.title"
                sx={{ mb: { xs: 0, md: 0.5 } }}
                {...titleTypographyProps}
              >
                {title}
              </Typography>
            )}
          </Stack>
          {subtitle && (
            <Typography
              variant="disclaimer1"
              display="block"
              data-testid="QuickLink.subtitle"
              {...subtitleTypographyProps}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {buttonTitle && (
          <Button
            variant="navigation"
            color="secondary"
            endIcon={<ArrowRightIcon />}
            data-testid="QuickLink.button"
            {...buttonProps}
          >
            {buttonTitle}
          </Button>
        )}
      </Stack>
    );
  },
);

export interface QuickLinkWrapperProps extends BoxProps<ElementType> {}

/**
 * Wrapper container for 3 QuickLinks
 */
export const QuickLinkWrapper: React.FC<QuickLinkWrapperProps> = forwardRef(({ sx, children, ...rest }, ref) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, 1fr)' },
        gap: 3,
        ...sx,
      }}
      ref={ref}
      data-testid="QuickLink.wrapper"
      {...rest}
    >
      {children}
    </Box>
  );
});
